import { useCentralizedMe } from '@crehana/auth';

import type { TimeAttendanceMarkType } from '@/generated/graphql/types';

import { useTimeAndAttendanceUserAttendanceRecordAttendanceMarkMutation } from './record-attendance-mark.v5.generated';
import { useRecordAttendanceMarkMock } from './use-record-attendance-mark.mock';
import type {
  TMarkResult,
  TUseRecordAttendanceMarkReturn,
} from './use-record-attendance-mark.types';

export type { TMarkResult } from './use-record-attendance-mark.types';

export const useRecordAttendanceMark = ({
  timeZone,
}: {
  timeZone: string;
}): TUseRecordAttendanceMarkReturn => {
  const mock = useRecordAttendanceMarkMock();

  const { currentPageOrganization } = useCentralizedMe();
  const orgId = currentPageOrganization.id;

  const [mutate, { loading }] =
    useTimeAndAttendanceUserAttendanceRecordAttendanceMarkMutation({
      context: { clientName: 'v5' },
    });

  if (mock) {
    return mock;
  }

  const recordMark = async (
    markType: TimeAttendanceMarkType,
  ): Promise<TMarkResult> => {
    try {
      const { data } = await mutate({
        variables: {
          input: {
            centralized_organization_id: orgId,
            mark_type: markType,
            timezone: timeZone,
          },
        },
      });

      const response = data?.time?.attendance_marks?.record_attendance_mark;

      if (
        response?.__typename === 'TimeRecordAttendanceMarkPayload' &&
        response.success
      ) {
        return {
          success: true,
          mark: response.mark,
          context: response.context,
        };
      }

      if (response && 'code' in response) {
        return {
          success: false,
          errorCode: response.code,
          message: response.message,
        };
      }

      return {
        success: false,
        errorCode: 'UNKNOWN',
        message: 'An unknown error occurred',
      };
    } catch (err) {
      return {
        success: false,
        errorCode: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'Network error',
      };
    }
  };

  return { recordMark, loading };
};
